import React, { useCallback, useEffect, useState } from "react";
import { Check, Loader2, MessageSquareText, RefreshCw, Star, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime, WorkbenchHeader } from "@/components/admin/AdminWorkbenchUi";

const STATUSES = ["pending", "published", "rejected"];
const TYPES = ["all", "property", "agent"];

export default function AdminReviews() {
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workingId, setWorkingId] = useState("");
  const [rejectingId, setRejectingId] = useState("");
  const [notes, setNotes] = useState({});

  // Moderation filters are enforced again by the backend allowlist; these
  // controls only select which trusted queue the admin wants to inspect.
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/reviews?status=${status}&type=${type}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status, type]);

  useEffect(() => { load(); }, [load]);

  // The review_type is supplied by the combined admin queue so the backend can
  // update the correct property/agent table and write moderation metadata.
  const moderate = async (review, nextStatus) => {
    setWorkingId(review.id);
    try {
      await api.put(`/admin/reviews/${review.review_type}/${review.id}`, {
        status: nextStatus,
        moderation_note: notes[review.id]?.trim() || null,
      });
      toast.success(nextStatus === "published" ? "Review published" : "Review rejected");
      setRejectingId("");
      setItems((current) => current.filter((item) => item.id !== review.id));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWorkingId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-review-moderation">
      <WorkbenchHeader
        eyebrow="Trust & safety"
        title="Review Moderation"
        description="Publish genuine client feedback and reject abusive or misleading submissions before they appear publicly."
        actions={<Button variant="outline" onClick={load} disabled={loading} title="Refresh review queue" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" /> Refresh
        </Button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="inline-flex rounded-xl border border-border bg-card p-1 self-start">
          {STATUSES.map((value) => (
            <button key={value} type="button" title={`Show ${value} reviews`} onClick={() => setStatus(value)} aria-pressed={status === value}
              className={`rounded-lg px-4 py-2 text-xs font-bold capitalize transition ${status === value ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {value}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-xl border border-border bg-card p-1 self-start">
          {TYPES.map((value) => (
            <button key={value} type="button" title={`Filter by ${value} reviews`} onClick={() => setType(value)} aria-pressed={type === value}
              className={`rounded-lg px-4 py-2 text-xs font-bold capitalize transition ${type === value ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {value}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center min-h-64 rounded-3xl border border-border bg-card">
          <div className="flex items-center gap-3 text-sm font-semibold text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin text-primary" /> Loading reviews…</div>
        </div>
      ) : error ? (
        <Card className="p-8 text-center rounded-3xl">
          <p className="font-bold text-destructive">Could not load the moderation queue</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <Button className="mt-5" onClick={load} title="Retry loading reviews">Try again</Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center rounded-3xl border-dashed">
          <MessageSquareText className="w-10 h-10 mx-auto text-muted-foreground/60" />
          <h2 className="font-display text-xl font-bold mt-4">No {status} reviews</h2>
          <p className="text-sm text-muted-foreground mt-1">This queue is clear.</p>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((review) => (
            <Card key={`${review.review_type}-${review.id}`} className="p-5 rounded-2xl shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="capitalize">{review.review_type}</Badge>
                    <span className="text-xs text-muted-foreground">{review.target_name || "Unavailable target"}</span>
                  </div>
                  <p className="font-bold mt-2">{review.reviewer_name || "ShreeHomes user"}</p>
                </div>
                <div className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-black text-amber-700 dark:text-amber-300">
                  <Star className="w-3.5 h-3.5 fill-current" aria-hidden="true" /> {review.rating}/5
                </div>
              </div>
              <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{review.comment}</p>
              <p className="text-xs text-muted-foreground">Submitted {formatDateTime(review.created_at)}</p>

              {status === "pending" && (
                <div className="pt-3 border-t border-border">
                  {rejectingId === review.id && (
                    <Textarea value={notes[review.id] || ""} maxLength={500}
                      onChange={(event) => setNotes((current) => ({ ...current, [review.id]: event.target.value }))}
                      placeholder="Optional internal rejection reason" className="mb-3" data-testid={`review-note-${review.id}`} />
                  )}
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" title="Reject this review" disabled={workingId === review.id}
                      onClick={() => rejectingId === review.id ? moderate(review, "rejected") : setRejectingId(review.id)} className="gap-2">
                      {workingId === review.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                      {rejectingId === review.id ? "Confirm reject" : "Reject"}
                    </Button>
                    <Button title="Approve and publish this review" disabled={workingId === review.id}
                      onClick={() => moderate(review, "published")} className="gap-2">
                      {workingId === review.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Publish
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
